/**
 * DAGNodeStatusIndicator Component - Node Status Display
 *
 * FERP CRITICAL: Domain-agnostic node status visualization
 * Life-critical status indicators with color coding
 *
 * Principles:
 * - All data from backend via useDAGExecution hook (no hardcoded values)
 * - Repository pattern (GraphQL queries only)
 * - Color-coded status indicators for quick visual assessment
 * - Criticality-based styling (life-critical = red border)
 * - Backend ID generation (never generate node IDs client-side)
 * - Immutable audit trails for status transitions
 * - 16 primitive types (DAG is workflow execution primitive)
 */

import React from 'react';
import { Badge, Group, Paper, Stack, Text, Tooltip, Box } from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconClock,
  IconPlayerPause,
  IconAlertCircle,
  IconCircleDashed,
  IconLock,
} from '@tabler/icons-react';
import {
  DAGNodeStatusEnum,
  DAGNodeTypeEnum,
  DAGCriticalityEnum,
} from './hooks/useDAGExecution';

// ============================================================
// TypeScript Types
// ============================================================

export interface DAGNodeStatusIndicatorProps {
  nodeId: string;
  label: string;
  status: DAGNodeStatusEnum;
  nodeType: DAGNodeTypeEnum;
  criticality: DAGCriticalityEnum;
  durationMs?: number;
  errorMessage?: string;
  onClick?: () => void;
}

// ============================================================
// Status Icon Mapping
// ============================================================

function getStatusIcon(status: DAGNodeStatusEnum): React.ReactNode {
  switch (status) {
    case DAGNodeStatusEnum.Pending:
      return <IconClock size={16} />;
    case DAGNodeStatusEnum.Running:
      return <IconCircleDashed size={16} />;
    case DAGNodeStatusEnum.Completed:
      return <IconCheck size={16} />;
    case DAGNodeStatusEnum.Failed:
      return <IconX size={16} />;
    case DAGNodeStatusEnum.Skipped:
      return <IconPlayerPause size={16} />;
    case DAGNodeStatusEnum.Blocked:
      return <IconLock size={16} />;
    default:
      return <IconAlertCircle size={16} />;
  }
}

// ============================================================
// Status Color Mapping
// ============================================================

function getStatusColor(status: DAGNodeStatusEnum): string {
  switch (status) {
    case DAGNodeStatusEnum.Pending:
      return 'gray';
    case DAGNodeStatusEnum.Running:
      return 'blue';
    case DAGNodeStatusEnum.Completed:
      return 'green';
    case DAGNodeStatusEnum.Failed:
      return 'red';
    case DAGNodeStatusEnum.Skipped:
      return 'yellow';
    case DAGNodeStatusEnum.Blocked:
      return 'orange';
    default:
      return 'gray';
  }
}

// ============================================================
// Criticality Color Mapping
// ============================================================

function getCriticalityColor(criticality: DAGCriticalityEnum): string {
  switch (criticality) {
    case DAGCriticalityEnum.Low:
      return 'gray';
    case DAGCriticalityEnum.Medium:
      return 'blue';
    case DAGCriticalityEnum.High:
      return 'orange';
    case DAGCriticalityEnum.Critical:
      return 'red';
    default:
      return 'gray';
  }
}

// ============================================================
// Node Type Icon Mapping
// ============================================================

function getNodeTypeLabel(nodeType: DAGNodeTypeEnum): string {
  switch (nodeType) {
    case DAGNodeTypeEnum.Task:
      return 'Task';
    case DAGNodeTypeEnum.Decision:
      return 'Decision';
    case DAGNodeTypeEnum.Parallel:
      return 'Parallel';
    case DAGNodeTypeEnum.Sequential:
      return 'Sequential';
    case DAGNodeTypeEnum.Conditional:
      return 'Conditional';
    default:
      return 'Unknown';
  }
}

// ============================================================
// Component Implementation
// ============================================================

export function DAGNodeStatusIndicator({
  nodeId,
  label,
  status,
  nodeType,
  criticality,
  durationMs,
  errorMessage,
  onClick,
}: DAGNodeStatusIndicatorProps) {
  const statusColor = getStatusColor(status);
  const statusIcon = getStatusIcon(status);
  const criticalityColor = getCriticalityColor(criticality);
  const nodeTypeLabel = getNodeTypeLabel(nodeType);

  // Life-critical indicator (red border)
  const isLifeCritical = criticality === DAGCriticalityEnum.Critical;

  return (
    <Paper
      p="sm"
      withBorder
      style={{
        cursor: onClick ? 'pointer' : 'default',
        borderColor: isLifeCritical ? '#fa5252' : undefined,
        borderWidth: isLifeCritical ? 2 : 1,
      }}
      onClick={onClick}
    >
      <Stack gap="xs">
        {/* Node Label and ID */}
        <Group justify="space-between">
          <Text size="sm" fw={600}>
            {label}
          </Text>
          <Text size="xs" c="dimmed">
            {nodeId}
          </Text>
        </Group>

        {/* Status and Type Badges */}
        <Group gap="xs">
          <Badge
            color={statusColor}
            variant="filled"
            leftSection={statusIcon}
            size="sm"
          >
            {status}
          </Badge>

          <Badge color="gray" variant="light" size="sm">
            {nodeTypeLabel}
          </Badge>

          {isLifeCritical && (
            <Tooltip label="Life-critical node - requires elevated monitoring">
              <Badge color={criticalityColor} variant="filled" size="sm">
                {criticality}
              </Badge>
            </Tooltip>
          )}
        </Group>

        {/* Duration Display */}
        {durationMs !== undefined && (
          <Text size="xs" c="dimmed">
            Duration: {(durationMs / 1000).toFixed(2)}s
          </Text>
        )}

        {/* Error Message */}
        {errorMessage && status === DAGNodeStatusEnum.Failed && (
          <Box
            p="xs"
            style={{
              backgroundColor: '#ffe3e3',
              borderRadius: 4,
            }}
          >
            <Text size="xs" c="red">
              Error: {errorMessage}
            </Text>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}
